import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Activity, Plus, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { supabaseHelpers } from '../lib/supabaseClient';
import ServiceCard from './ServiceCard';
import IncidentBanner from './IncidentBanner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { currentOrganization } = useOrganization();

  const [services, setServices] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [logs, setLogs] = useState({});
  const [uptimeData, setUptimeData] = useState({});
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Guard against setState after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [servicesData, endpointsData, activeIncidents] = await Promise.all([
        supabaseHelpers.services.getServices(currentOrganization.id),
        supabaseHelpers.endpoints.getAllEndpoints(currentOrganization.id),
        supabaseHelpers.incidents.getActiveIncidents(currentOrganization.id),
      ]);

      if (!mountedRef.current) return;
      setServices(servicesData);
      setEndpoints(endpointsData);
      setIncidents(activeIncidents);

      // Fetch logs and uptime in parallel for all endpoints
      const [logsResults, uptimeResults] = await Promise.all([
        Promise.allSettled(
          endpointsData.map(ep =>
            supabaseHelpers.monitorLogs.getEndpointLogs(ep.id, 1)
              .then(logs => ({ id: ep.id, log: logs[0] || null }))
          )
        ),
        Promise.allSettled(
          endpointsData.map(ep =>
            supabaseHelpers.monitorLogs.getEndpointUptime(ep.id, 24)
              .then(stats => ({ id: ep.id, uptime: stats.uptime !== null ? parseFloat(stats.uptime) : null }))
          )
        ),
      ]);

      if (!mountedRef.current) return;

      const logsMap = {};
      logsResults.forEach(result => {
        if (result.status === 'fulfilled' && result.value.log) {
          logsMap[result.value.id] = result.value.log;
        }
      });

      const uptimeMap = {};
      uptimeResults.forEach(result => {
        if (result.status === 'fulfilled') {
          uptimeMap[result.value.id] = result.value.uptime;
        }
      });

      setLogs(logsMap);
      setUptimeData(uptimeMap);
      setLastUpdate(new Date());
    } catch (err) {
      if (mountedRef.current) setError(err.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [currentOrganization, fetchData]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading && services.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-16 h-16 text-[#2C9C7A] animate-pulse mx-auto mb-4" />
          <p className="text-white text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalEndpoints = endpoints.length;
  let operationalCount = 0;
  endpoints.forEach(ep => { if (logs[ep.id]?.is_success) operationalCount++; });
  const overallStatus = operationalCount === totalEndpoints && totalEndpoints > 0;

  // Calculate true 24h average uptime across all endpoints with data
  const uptimeValues = Object.values(uptimeData).filter(v => v !== null);
  const avgUptime = uptimeValues.length > 0
    ? (uptimeValues.reduce((a, b) => a + b, 0) / uptimeValues.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B]">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes blob {
            0% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          .animate-blob { animation: blob 7s infinite; }
          .animation-delay-2000 { animation-delay: 2s; }
          .animation-delay-4000 { animation-delay: 4s; }
        `,
      }} />

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#2C9C7A] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#3599B8] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute top-40 left-40 w-80 h-80 bg-[#5E4B8B] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10">
        <header className="border-b border-white/10 backdrop-blur-md">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-[#2C9C7A] to-[#3599B8] rounded-xl">
                  <Activity className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">PulseLab Dashboard</h1>
                  <p className="text-sm text-purple-200">
                    Real-time monitoring for {currentOrganization?.name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-purple-200">Last checked</p>
                  <p className="text-white font-medium">{lastUpdate.toLocaleTimeString()}</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg text-white transition-all duration-200 border border-white/20"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </header>

        <section className="py-12 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className={`rounded-2xl p-8 ${
              overallStatus
                ? 'bg-gradient-to-r from-[#2C9C7A]/20 to-[#3599B8]/20 border border-[#2C9C7A]/30'
                : 'bg-gradient-to-r from-[#B0413E]/20 to-[#3599B8]/20 border border-[#B0413E]/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    {overallStatus
                      ? <CheckCircle className="w-10 h-10 text-[#2C9C7A]" />
                      : <AlertCircle className="w-10 h-10 text-[#B0413E]" />
                    }
                    <h2 className="text-3xl font-bold text-white">
                      {overallStatus ? 'All Systems Operational' : 'Experiencing Issues'}
                    </h2>
                  </div>
                  <p className="text-lg text-gray-300">
                    {operationalCount} of {totalEndpoints} endpoints online
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-white">
                    {avgUptime !== null ? `${avgUptime}%` : '--'}
                  </div>
                  <p className="text-gray-300">Avg uptime (24h)</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {incidents.length > 0 && (
          <section className="pb-8">
            <div className="container mx-auto px-4">
              <div className="mb-4">
                <h3 className="text-2xl font-semibold text-white mb-2">
                  Active Incidents ({incidents.length})
                </h3>
                <p className="text-gray-400">Critical issues requiring attention</p>
              </div>
              <IncidentBanner incidents={incidents} />
            </div>
          </section>
        )}

        <section className="pb-12">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-white">Services</h3>
                <p className="text-gray-400">
                  {services.length} service{services.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => navigate('/admin/add-endpoint')}
                className="flex items-center gap-2 px-4 py-2 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Endpoint
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-[#B0413E]/10 border border-[#B0413E]/20 rounded-lg">
                <p className="text-[#B0413E]">{error}</p>
              </div>
            )}

            {services.length === 0 ? (
              <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-12 text-center">
                <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-white mb-2">No services yet</h4>
                <p className="text-gray-400 mb-6">Create your first service to start monitoring endpoints</p>
                <button
                  onClick={() => navigate('/admin/add-endpoint')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add First Endpoint
                </button>
              </div>
            ) : (
              <div className="grid gap-6">
                {services.map(service => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    endpoints={endpoints}
                    logs={logs}
                    uptimeData={uptimeData}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
